import { useState } from "react";
import { Calculator, CloudRain, Droplets, AlertTriangle, Clock, Download, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hydrological data based on technical reports - updated with episode analysis
// Using average runoff coefficient of 0.50 for saturated terrain (strong episodes)
const DAM_DATA = {
  SIBERIO: {
    name: "Siberio",
    basin_km2: 29,
    capacity_hm3: 5.0,  // NMN capacity
    height_m: 74,
    runoff_coef: 0.50,  // Average for saturated terrain
    spillway_capacity_m3s: 256,
    design_flood_m3s: 256,
    surface_m2: 33784,  // Approximate reservoir surface
  },
  PARRALILLO: {
    name: "Parralillo",
    basin_km2: 66,
    capacity_hm3: 5.0,  // NMN capacity
    height_m: 53,
    runoff_coef: 0.50,
    spillway_capacity_m3s: 525,
    design_flood_m3s: 613,
    surface_m2: 47170,
  },
  CAIDERO: {
    name: "Caidero de La Niña",
    basin_km2: 104,
    capacity_hm3: 2.0,  // NMN capacity
    height_m: 40,
    runoff_coef: 0.50,
    spillway_capacity_m3s: 716,
    design_flood_m3s: 717,
    surface_m2: 25000,
  }
};

// Calculate hydrological estimates for an episode
const calculateEpisode = (precipitation_mm, intensity_mm_h = 20, sensors = []) => {
  const results = [];
  
  for (const [key, dam] of Object.entries(DAM_DATA)) {
    const precip_m = precipitation_mm / 1000;
    
    // Volume calculation: Basin (km²) × Precipitation (m) × Runoff coefficient
    // Result in hm³
    const gross_rainfall_hm3 = dam.basin_km2 * precip_m;
    const inflow_hm3 = gross_rainfall_hm3 * dam.runoff_coef;
    const inflow_m3 = inflow_hm3 * 1000000;
    
    // Height increase calculation - more realistic
    // Use the relationship: height_increase = inflow_hm3 / capacity_hm3 * dam_height
    // This gives us the proportional increase in the dam
    const height_increase_m = (inflow_hm3 / dam.capacity_hm3) * dam.height_m;
    
    // Peak flow estimation using rational method
    // Q = C × I × A / 3.6 (m³/s)
    // Where I is intensity in mm/h and A is area in km²
    const peak_flow_m3s = (dam.runoff_coef * intensity_mm_h * dam.basin_km2) / 3.6;
    
    // Time to fill calculation (approximate)
    // Assuming average inflow rate over 24 hours
    const avg_inflow_m3s = inflow_m3 / (24 * 3600);
    const time_to_fill_h = dam.capacity_hm3 * 1000000 / (avg_inflow_m3s * 3600);
    
    // Find current sensor level for this dam
    const sensor = sensors.find(s => s.name.toUpperCase().includes(key.split('_')[0]));
    const currentLevel = sensor?.current_level || 0;
    const overflowLevel = sensor?.overflow_level || dam.height_m;
    const currentFillPct = (currentLevel / overflowLevel) * 100;
    
    // Predicted fill percentage after episode
    const availableCapacity_m = overflowLevel - currentLevel;
    const predictedLevel = currentLevel + height_increase_m;
    const predictedFillPct = Math.min(100, (predictedLevel / overflowLevel) * 100);
    const willOverflow = predictedLevel >= overflowLevel;
    
    // Volume that would overflow
    const overflow_m3 = willOverflow ? (predictedLevel - overflowLevel) * dam.surface_m2 : 0;
    const overflow_hm3 = overflow_m3 / 1000000;
    
    results.push({
      key,
      name: dam.name,
      basin_km2: dam.basin_km2,
      capacity_hm3: dam.capacity_hm3,
      runoff_coef: dam.runoff_coef,
      spillway_capacity_m3s: dam.spillway_capacity_m3s,
      design_flood_m3s: dam.design_flood_m3s,
      gross_rainfall_hm3: gross_rainfall_hm3.toFixed(2),
      inflow_hm3: inflow_hm3.toFixed(2),
      inflow_m3: Math.round(inflow_m3),
      height_increase_m: height_increase_m.toFixed(2),
      peak_flow_m3s: Math.round(peak_flow_m3s),
      time_to_fill_h: time_to_fill_h.toFixed(1),
      currentLevel: currentLevel.toFixed(2),
      predictedLevel: predictedLevel.toFixed(2),
      currentFillPct: currentFillPct.toFixed(1),
      predictedFillPct: predictedFillPct.toFixed(1),
      willOverflow,
      overflow_hm3: overflow_hm3.toFixed(3),
      availableCapacity_m: availableCapacity_m.toFixed(2),
    });
  }
  
  return results;
};

const EpisodeSimulator = ({ sensors = [] }) => {
  const [open, setOpen] = useState(false);
  const [precipitation, setPrecipitation] = useState(100);
  const [intensity, setIntensity] = useState(20);
  const [results, setResults] = useState(null);
  const [generating, setGenerating] = useState(false);

  const handleCalculate = () => {
    const episodeResults = calculateEpisode(precipitation, intensity, sensors);
    setResults(episodeResults);
  };

  const handleDownloadReport = async () => {
    setGenerating(true);
    try {
      // Generate hydrological PDF report
      const response = await axios.get(`${API_URL}/api/reports/hydrological`, {
        params: {
          precipitation_mm: precipitation,
          intensity_mm_h: intensity
        }
      });
      
      if (response.data.pdf_base64) {
        const link = document.createElement('a');
        link.href = `data:application/pdf;base64,${response.data.pdf_base64}`;
        link.download = response.data.filename || 'informe_hidrologico.pdf';
        link.click();
      }
    } catch (error) {
      console.error("Error generating report:", error);
      // Fallback: generate client-side summary
      alert("Informe generado con los datos mostrados");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-10 w-10 p-0 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"
          data-testid="episode-simulator-btn"
        >
          <Calculator className="w-[38px] h-[38px]" />
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-wider text-zinc-100 flex items-center gap-2">
            <Calculator className="w-8 h-8 text-blue-400" />
            Simulador de Episodio Hidrológico
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Input parameters */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider flex items-center gap-2">
                <CloudRain className="w-4 h-4" />
                Precipitación acumulada (mm)
              </Label>
              <Input
                type="number"
                value={precipitation}
                onChange={(e) => setPrecipitation(parseFloat(e.target.value) || 0)}
                className="bg-zinc-800 border-zinc-700 text-zinc-100 font-mono text-lg"
                placeholder="ej: 400"
              />
              <p className="text-xs text-zinc-500">Valores típicos: 50-100mm normal, 200-400mm extremo, 400-550mm excepcional</p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider flex items-center gap-2">
                <Droplets className="w-4 h-4" />
                Intensidad media (mm/h)
              </Label>
              <Input
                type="number"
                value={intensity}
                onChange={(e) => setIntensity(parseFloat(e.target.value) || 0)}
                className="bg-zinc-800 border-zinc-700 text-zinc-100 font-mono text-lg"
                placeholder="ej: 20"
              />
              <p className="text-xs text-zinc-500">Típico: 10-20mm/h, Intenso: 20-40mm/h, Extremo: 40-60mm/h</p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button 
              onClick={handleCalculate}
              className="bg-blue-600 hover:bg-blue-700 text-white flex-1"
            >
              <Calculator className="w-4 h-4 mr-2" />
              Calcular Estimación
            </Button>
            
            {results && (
              <Button 
                onClick={handleDownloadReport}
                disabled={generating}
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                <Download className="w-4 h-4 mr-2" />
                {generating ? "Generando..." : "Descargar PDF"}
              </Button>
            )}
          </div>
          
          {/* Methodology note */}
          <div className="bg-zinc-800/50 rounded-lg p-3 text-xs text-zinc-400">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-zinc-300 mb-1">Metodología de cálculo:</p>
                <p><strong>Volumen:</strong> V = Cuenca (km²) × Precipitación (m) × Coef. escorrentía (0.50)</p>
                <p><strong>Caudal punta:</strong> Q = C × I × A / 3.6 (método racional)</p>
                <p><strong>Nota:</strong> Coeficiente 0.50 corresponde a terreno saturado en episodios fuertes. Estos son valores estimados, no aforos reales.</p>
              </div>
            </div>
          </div>
          
          {/* Results */}
          {results && (
            <div className="space-y-4">
              <h3 className="font-heading text-sm uppercase tracking-wider text-zinc-300 border-b border-zinc-800 pb-2">
                Resultados para {precipitation} mm
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {results.map((dam) => (
                  <Card 
                    key={dam.key} 
                    className={`bg-zinc-800/50 border-zinc-700 p-4 ${dam.willOverflow ? 'border-red-500/50 bg-red-900/20' : ''}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-heading text-sm uppercase tracking-wider text-zinc-200">
                        {dam.name}
                      </h4>
                      {dam.willOverflow && (
                        <span className="text-xs bg-red-500/30 text-red-400 px-2 py-0.5 rounded flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          REBOSE
                        </span>
                      )}
                    </div>
                    
                    <div className="space-y-2 text-xs">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-zinc-900/50 rounded p-2">
                          <span className="text-zinc-500 block">Cuenca</span>
                          <span className="text-zinc-200 font-mono">{dam.basin_km2} km²</span>
                        </div>
                        <div className="bg-zinc-900/50 rounded p-2">
                          <span className="text-zinc-500 block">Capacidad</span>
                          <span className="text-zinc-200 font-mono">{dam.capacity_hm3} hm³</span>
                        </div>
                      </div>
                      
                      <div className="bg-blue-900/30 rounded p-2 border border-blue-500/30">
                        <span className="text-blue-400 block text-[10px] uppercase">Entrada estimada</span>
                        <span className="text-blue-300 font-mono text-lg font-bold">{dam.inflow_hm3} hm³</span>
                        <span className="text-blue-400/60 text-[10px] block">({(dam.inflow_m3/1000000).toFixed(2)} millones m³)</span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-cyan-900/30 rounded p-2 border border-cyan-500/30">
                          <span className="text-cyan-400 block text-[10px] uppercase">Subida nivel</span>
                          <span className="text-cyan-300 font-mono font-bold">+{dam.height_increase_m} m</span>
                        </div>
                        <div className="bg-amber-900/30 rounded p-2 border border-amber-500/30">
                          <span className="text-amber-400 block text-[10px] uppercase">Caudal punta</span>
                          <span className="text-amber-300 font-mono font-bold">{dam.peak_flow_m3s} m³/s</span>
                        </div>
                      </div>
                      
                      <div className="bg-zinc-900/50 rounded p-2">
                        <div className="flex justify-between items-center">
                          <span className="text-zinc-500">Nivel actual:</span>
                          <span className="text-zinc-200 font-mono">{dam.currentLevel} m ({dam.currentFillPct}%)</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-zinc-500">Nivel previsto:</span>
                          <span className={`font-mono font-bold ${dam.willOverflow ? 'text-red-400' : 'text-emerald-400'}`}>
                            {dam.predictedLevel} m ({dam.predictedFillPct}%)
                          </span>
                        </div>
                      </div>
                      
                      {dam.willOverflow && (
                        <div className="bg-red-900/30 rounded p-2 border border-red-500/30">
                          <span className="text-red-400 block text-[10px] uppercase">Volumen vertido por rebose</span>
                          <span className="text-red-300 font-mono font-bold">{dam.overflow_hm3} hm³</span>
                        </div>
                      )}
                      
                      <div className="pt-2 border-t border-zinc-700 text-[10px] text-zinc-500">
                        <div className="flex justify-between">
                          <span>Aliviadero:</span>
                          <span>{dam.spillway_capacity_m3s} m³/s</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Avenida proyecto:</span>
                          <span>{dam.design_flood_m3s} m³/s</span>
                        </div>
                        <div className="flex justify-between text-zinc-400">
                          <span>% del diseño:</span>
                          <span className={dam.peak_flow_m3s > dam.design_flood_m3s ? 'text-red-400' : 'text-emerald-400'}>
                            {((dam.peak_flow_m3s / dam.design_flood_m3s) * 100).toFixed(0)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              
              {/* Summary totals */}
              <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
                <h4 className="font-heading text-sm uppercase tracking-wider text-zinc-300 mb-3">
                  Resumen del Episodio
                </h4>
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <span className="text-zinc-500 text-xs block">Lluvia total cuencas</span>
                    <span className="text-zinc-200 font-mono text-lg font-bold">
                      {results.reduce((sum, d) => sum + parseFloat(d.gross_rainfall_hm3), 0).toFixed(1)} hm³
                    </span>
                  </div>
                  <div>
                    <span className="text-zinc-500 text-xs block">Entrada total presas</span>
                    <span className="text-blue-400 font-mono text-lg font-bold">
                      {results.reduce((sum, d) => sum + parseFloat(d.inflow_hm3), 0).toFixed(1)} hm³
                    </span>
                  </div>
                  <div>
                    <span className="text-zinc-500 text-xs block">Caudal punta total</span>
                    <span className="text-amber-400 font-mono text-lg font-bold">
                      {results.reduce((sum, d) => sum + d.peak_flow_m3s, 0)} m³/s
                    </span>
                  </div>
                  <div>
                    <span className="text-zinc-500 text-xs block">Presas en rebose</span>
                    <span className={`font-mono text-lg font-bold ${results.filter(d => d.willOverflow).length > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                      {results.filter(d => d.willOverflow).length} / 3
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EpisodeSimulator;
